import {
  VestingGraphPoint,
  VestingGraphPointStatus,
} from '../../../../../hooks/vesting-task/use-vesting-graph';
import { MilestoneSegmentBound, VestingGraphSeriesPoint } from './vesting-graph.types';

/** Equal-width target columns — dates are labels, not a timeline. */
export const resolveMilestoneSegmentBounds = ({
  domainMax,
  domainMin,
  points,
}: {
  domainMax: number;
  domainMin: number;
  points: VestingGraphPoint[];
}): MilestoneSegmentBound[] => {
  const domainSpan = Math.max(domainMax - domainMin, 1);
  const columnSpan = domainSpan / Math.max(points.length, 1);

  return points.map((_, index) => {
    const isLast = index === points.length - 1;

    return {
      endTimestamp: isLast ? domainMax : domainMin + (index + 1) * columnSpan,
      startTimestamp: domainMin + index * columnSpan,
    };
  });
};

export const getTimestampPosition = ({
  maxTimestamp,
  minTimestamp,
  timestamp,
}: {
  maxTimestamp: number;
  minTimestamp: number;
  timestamp: number;
}) => {
  if (maxTimestamp <= minTimestamp) return 0;
  return ((timestamp - minTimestamp) / (maxTimestamp - minTimestamp)) * 100;
};

export const calendarTimeOf = (point: { calendarTimestamp?: number; timestamp: number }) =>
  point.calendarTimestamp ?? point.timestamp;

export const isEventLayoutPoint = (point: {
  isAccelerationColumn?: boolean;
  isExpiryColumn?: boolean;
}) => Boolean(point.isAccelerationColumn || point.isExpiryColumn);

/** Markers are positioned against a real datum, so an off-schedule date snaps to the nearest. */
export const findTimestampIndex = (
  points: Array<{
    calendarTimestamp?: number;
    isAccelerationColumn?: boolean;
    isExpiryColumn?: boolean;
    timestamp: number;
  }>,
  date?: string,
  { ignoreEventColumns = false }: { ignoreEventColumns?: boolean } = {},
) => {
  if (!date) return null;

  const targetTime = new Date(date).getTime();
  const eligibleIndexes = points
    .map((point, index) => index)
    .filter((index) => !ignoreEventColumns || !isEventLayoutPoint(points[index]!));
  if (eligibleIndexes.length === 0) return null;

  const exactIndex = eligibleIndexes.find((index) => calendarTimeOf(points[index]!) === targetTime);
  if (exactIndex !== undefined) return exactIndex;

  let closestIndex = eligibleIndexes[0]!;
  let closestDistance = Number.POSITIVE_INFINITY;

  eligibleIndexes.forEach((index) => {
    const distance = Math.abs(calendarTimeOf(points[index]!) - targetTime);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = index;
    }
  });

  return closestIndex;
};

/** The dedicated acceleration slot — never the payment that shares its calendar date. */
export const findAccelerationColumnIndex = (points: Array<{ isAccelerationColumn?: boolean }>) => {
  const index = points.findIndex((point) => point.isAccelerationColumn);
  return index >= 0 ? index : null;
};

/** The dedicated expiry slot — never the payment that shares its calendar date. */
export const findExpiryColumnIndex = (points: Array<{ isExpiryColumn?: boolean }>) => {
  const index = points.findIndex((point) => point.isExpiryColumn);
  return index >= 0 ? index : null;
};

export const resolveEventBandSpan = ({
  domainMax,
  domainMin,
  plotWidth,
  timestamp,
  widthPx,
}: {
  domainMax: number;
  domainMin: number;
  plotWidth: number;
  timestamp: number;
  widthPx: number;
}) => {
  const halfSpan = ((domainMax - domainMin) * (widthPx / Math.max(plotWidth, 1))) / 2;

  return { endTimestamp: timestamp + halfSpan, startTimestamp: timestamp - halfSpan };
};

/**
 * The bar the grant vests next, which the chart outlines with a dashed “current” column.
 * Months that release nothing — a cliff still running, or the gap between two vesting
 * dates — are passed over so the outline lands on an actual vesting event. When
 * acceleration has already fired, columns on or after that date stay inactive and never
 * take the outline; a future acceleration date must not hide the next vest before it.
 */
export const findNextVestIndex = (
  points: VestingGraphSeriesPoint[],
  { beforeTimestamp }: { beforeTimestamp?: null | number } = {},
) => {
  const isEligible = (point: VestingGraphSeriesPoint, requireShares: boolean) => {
    if (point.status !== VestingGraphPointStatus.UNVESTED) return false;
    if (point.isAccelerationColumn || point.isExpiryColumn) return false;
    if (requireShares && point.periodShares <= 0) return false;
    if (beforeTimestamp != null && calendarTimeOf(point) >= beforeTimestamp) return false;

    return true;
  };

  const paymentIndex = points.findIndex((point) => isEligible(point, true));
  if (paymentIndex >= 0) return paymentIndex;

  // Dynamic plateaus can sit in UNVESTED months with 0 periodShares before the next
  // frequency step — still mark the current open column so the chart matches Standard.
  const openIndex = points.findIndex((point) => isEligible(point, false));

  return openIndex < 0 ? null : openIndex;
};

export const isAccelerationInactivePoint = ({
  accelerationTimestamp,
  point,
}: {
  accelerationTimestamp?: null | number;
  point: Pick<VestingGraphSeriesPoint, 'isAccelerationColumn' | 'isExpiryColumn' | 'timestamp'>;
}) => {
  if (accelerationTimestamp == null || point.isAccelerationColumn || point.isExpiryColumn) {
    return false;
  }

  return point.timestamp > accelerationTimestamp;
};

const lerpTimestamp = ({
  fromMax,
  fromMin,
  toMax,
  toMin,
  value,
}: {
  fromMax: number;
  fromMin: number;
  toMax: number;
  toMin: number;
  value: number;
}) => {
  if (fromMax === fromMin) return toMin;
  const ratio = Math.min(Math.max((value - fromMin) / (fromMax - fromMin), 0), 1);

  return toMin + ratio * (toMax - toMin);
};

/**
 * Maps an accel/expiry calendar date onto the same x-space as milestone segment edges.
 * Required when equal-column layout remaps segment `timestamp` away from real dates —
 * raw calendar x would otherwise sit left of a later column and zigzag the Area.
 */
export const resolveMilestoneEventLayoutTimestamp = ({
  calendarTimestamp,
  domainMax,
  domainMin,
  milestoneDates,
  segmentBounds,
}: {
  calendarTimestamp: number;
  domainMax: number;
  domainMin: number;
  milestoneDates: number[];
  segmentBounds: MilestoneSegmentBound[];
}) => {
  if (milestoneDates.length === 0 || segmentBounds.length === 0) return calendarTimestamp;

  const firstMilestone = milestoneDates[0]!;
  if (calendarTimestamp <= firstMilestone) {
    return lerpTimestamp({
      fromMax: firstMilestone,
      fromMin: domainMin,
      toMax: segmentBounds[0]!.endTimestamp,
      toMin: domainMin,
      value: calendarTimestamp,
    });
  }

  for (let index = 0; index < milestoneDates.length - 1; index += 1) {
    const calendarStart = milestoneDates[index]!;
    const calendarEnd = milestoneDates[index + 1]!;
    if (calendarTimestamp > calendarEnd) continue;

    const layoutStart = segmentBounds[index]!.endTimestamp;
    const layoutEnd = segmentBounds[index + 1]!.startTimestamp;

    return lerpTimestamp({
      fromMax: calendarEnd,
      fromMin: calendarStart,
      toMax: Math.max(layoutStart, layoutEnd),
      toMin: layoutStart,
      value: calendarTimestamp,
    });
  }

  const lastIndex = milestoneDates.length - 1;

  return lerpTimestamp({
    fromMax: Math.max(domainMax, calendarTimestamp),
    fromMin: milestoneDates[lastIndex]!,
    toMax: domainMax,
    toMin: segmentBounds[lastIndex]!.endTimestamp,
    value: calendarTimestamp,
  });
};

export const layoutTimestampForDate = (
  chartPoints:
    | Array<{
        calendarTimestamp?: number;
        isAccelerationColumn?: boolean;
        isExpiryColumn?: boolean;
        timestamp: number;
      }>
    | undefined,
  date: string,
  { ignoreEventColumns = false }: { ignoreEventColumns?: boolean } = {},
) => {
  if (!chartPoints?.length) return new Date(date).getTime();

  const index = findTimestampIndex(chartPoints, date, { ignoreEventColumns });
  if (index === null) return new Date(date).getTime();

  return chartPoints[index]!.timestamp;
};
